type RuntimeCleanup = () => void

const MATRIX_THEME_IDS = new Set(['matrix', 'matrix-chrome'])
const SAKURA_THEME_IDS = new Set(['sakura', 'sakura-night', 'sakura-midnight'])

const RUNTIME_STYLE_ID = 'hp-theme-runtime-style'
const MATRIX_CANVAS_ID = 'hp-theme-matrix-rain-canvas'
const SAKURA_SVG_ID = 'hp-theme-sakura-petals-svg'

let activeThemeId: string | null = null
let runtimeCleanup: RuntimeCleanup | null = null

function isReducedMotionEnabled(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.body.classList.contains('force-reduce-motion')
  )
}

function ensureRuntimeStyles() {
  if (typeof document === 'undefined') return
  if (document.getElementById(RUNTIME_STYLE_ID)) return

  const style = document.createElement('style')
  style.id = RUNTIME_STYLE_ID
  style.textContent = `
    .hp-theme-runtime-overlay {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 17;
    }

    #${SAKURA_SVG_ID} {
      overflow: visible;
    }

    #${SAKURA_SVG_ID} .hp-sakura-petal {
      animation: hp-sakura-fall var(--fall-duration) linear var(--fall-delay) infinite;
      opacity: var(--petal-opacity);
      transform-box: fill-box;
      transform-origin: center;
    }

    @keyframes hp-sakura-fall {
      from {
        transform: translate(var(--from-x), var(--from-y)) rotate(0deg);
      }

      to {
        transform: translate(var(--to-x), var(--to-y)) rotate(360deg);
      }
    }
  `

  document.head.appendChild(style)
}

function mountMatrixRain(themeId: string): RuntimeCleanup {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => undefined
  }

  ensureRuntimeStyles()

  const stale = document.getElementById(MATRIX_CANVAS_ID)
  stale?.remove()

  const canvas = document.createElement('canvas')
  canvas.id = MATRIX_CANVAS_ID
  canvas.className = 'hp-theme-runtime-overlay'
  canvas.setAttribute('aria-hidden', 'true')
  canvas.style.opacity = themeId === 'matrix' ? '0.16' : '0.15'
  document.body.appendChild(canvas)

  const context = canvas.getContext('2d')
  if (!context) {
    canvas.remove()
    return () => undefined
  }

  const color = themeId === 'matrix' ? 'rgba(0, 255, 65, 0.62)' : 'rgba(117, 200, 201, 0.58)'
  const trail = themeId === 'matrix' ? 'rgba(10, 15, 10, 0.22)' : 'rgba(14, 23, 24, 0.24)'
  const glyphs = '01ABCDEFGHIJKLMNOPQRSTUVWXYZ#$%&*+<>{}[]/\\'
  const fontSize = themeId === 'matrix' ? 15 : 16

  let width = 0
  let height = 0
  let columns = 0
  let drops: number[] = []
  let speeds: number[] = []
  let frameId = 0

  const resize = () => {
    width = window.innerWidth
    height = window.innerHeight

    const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1))
    canvas.width = Math.max(1, Math.floor(width * dpr))
    canvas.height = Math.max(1, Math.floor(height * dpr))
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`

    context.setTransform(dpr, 0, 0, dpr, 0, 0)
    context.textBaseline = 'top'
    context.font = `${fontSize}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`

    columns = Math.max(1, Math.floor(width / fontSize))
    drops = Array.from({ length: columns }, () => Math.random() * (height / fontSize))
    speeds = Array.from({ length: columns }, () => 0.45 + Math.random() * 0.2)
  }

  const draw = () => {
    context.fillStyle = trail
    context.fillRect(0, 0, width, height)

    context.fillStyle = color

    for (let index = 0; index < columns; index += 1) {
      const glyph = glyphs[Math.floor(Math.random() * glyphs.length)]
      const x = index * fontSize
      const y = drops[index] * fontSize

      context.fillText(glyph, x, y)

      if (y > height && Math.random() > 0.985) {
        drops[index] = -Math.random() * 20
      } else {
        drops[index] += speeds[index]
      }
    }

    frameId = window.requestAnimationFrame(draw)
  }

  resize()
  draw()
  window.addEventListener('resize', resize)

  return () => {
    window.removeEventListener('resize', resize)
    if (frameId) {
      window.cancelAnimationFrame(frameId)
    }
    canvas.remove()
  }
}

function mountSakuraPetals(themeId: string): RuntimeCleanup {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => undefined
  }

  ensureRuntimeStyles()

  const stale = document.getElementById(SAKURA_SVG_ID)
  stale?.remove()

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('id', SAKURA_SVG_ID)
  svg.setAttribute('aria-hidden', 'true')
  svg.classList.add('hp-theme-runtime-overlay')
  document.body.appendChild(svg)

  const isLightSakura = themeId === 'sakura'
  const isMidnightSakura = themeId === 'sakura-midnight'
  const fillColor = isLightSakura ? '#F3A5CF' : isMidnightSakura ? '#C98CD9' : '#E75480'
  const strokeColor = isLightSakura ? 'rgba(191, 108, 153, 0.24)' : isMidnightSakura ? 'rgba(246, 233, 251, 0.34)' : 'rgba(255, 232, 240, 0.38)'
  const petalCount = isLightSakura ? 22 : isMidnightSakura ? 26 : 30

  const renderPetals = () => {
    const width = window.innerWidth
    const height = window.innerHeight

    while (svg.firstChild) {
      svg.removeChild(svg.firstChild)
    }

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`)

    for (let index = 0; index < petalCount; index += 1) {
      const fromX = Math.random() * width
      const toX = Math.max(-140, Math.min(width + 140, fromX + (Math.random() * 220 - 110)))
      const fromY = -120 - Math.random() * 140
      const toY = height + 160 + Math.random() * 140
      const scale = isLightSakura ? 0.65 + Math.random() * 0.75 : 0.7 + Math.random() * 0.9
      const opacityBase = isLightSakura ? 0.16 : 0.30
      const opacityRange = isLightSakura ? 0.12 : 0.32
      const opacity = opacityBase + Math.random() * opacityRange
      const durationBase = isLightSakura ? 12 : 9
      const durationRange = isLightSakura ? 8 : 8
      const duration = durationBase + Math.random() * durationRange
      const delay = -Math.random() * 14

      const petal = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      petal.setAttribute('d', 'M 0 0 C 4 -6, 12 -6, 16 0 C 12 6, 4 6, 0 0 Z')
      petal.setAttribute('fill', fillColor)
      petal.setAttribute('stroke', strokeColor)
      petal.setAttribute('stroke-width', isLightSakura ? '0.6' : '0.7')
      petal.classList.add('hp-sakura-petal')
      petal.style.setProperty('--from-x', `${fromX}px`)
      petal.style.setProperty('--to-x', `${toX}px`)
      petal.style.setProperty('--from-y', `${fromY}px`)
      petal.style.setProperty('--to-y', `${toY}px`)
      petal.style.setProperty('--petal-opacity', opacity.toFixed(2))
      petal.style.setProperty('--fall-duration', `${duration.toFixed(2)}s`)
      petal.style.setProperty('--fall-delay', `${delay.toFixed(2)}s`)
      petal.setAttribute('transform', `scale(${scale.toFixed(2)})`)

      svg.appendChild(petal)
    }
  }

  renderPetals()
  window.addEventListener('resize', renderPetals)

  return () => {
    window.removeEventListener('resize', renderPetals)
    svg.remove()
  }
}

export function applyRuntimeThemeEffects(themeId: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  if (activeThemeId === themeId) return

  clearRuntimeThemeEffects()
  
  // Skip effects if reduced motion is enabled
  if (isReducedMotionEnabled()) return

  activeThemeId = themeId

  if (MATRIX_THEME_IDS.has(themeId)) {
    runtimeCleanup = mountMatrixRain(themeId)
    return
  }

  if (SAKURA_THEME_IDS.has(themeId)) {
    runtimeCleanup = mountSakuraPetals(themeId)
    return
  }
}

export function clearRuntimeThemeEffects() {
  if (runtimeCleanup) {
    runtimeCleanup()
    runtimeCleanup = null
  }

  // We keep activeThemeId so we know which one to resume if refresh is called

  if (typeof document === 'undefined') return

  document.getElementById(MATRIX_CANVAS_ID)?.remove()
  document.getElementById(SAKURA_SVG_ID)?.remove()
}
